import { type DateRangePickerProps } from '@eppendorf/vnls-react-components';
import { ReportOutput, ReportStatus } from '@eppendorf/vnls-reporting-service-utils';
import { type SyntheticEvent, useCallback } from 'react';

import { ReportStatusFilter } from '$features/report-hub/components/status-filter';
import { useCopyHash } from '$features/report-hub/use-copy-hash';
import { useDeleteDialog } from '$features/report-hub/use-delete-dialog';
import { useReportDateRange } from '$features/report-hub/use-report-date-range';
import { useReportSearchParams } from '$features/report-hub/use-report-search-params';

type QuickSelectOption = NonNullable<
  NonNullable<DateRangePickerProps['quickSelect']>['selectedOption']
>;

export interface ReportListFilters {
  // Derived filter values for API calls
  apiStatus: ReportStatus | undefined;
  apiSearchQuery: string | undefined;
  from: string | undefined;
  to: string | undefined;

  // UI state
  statusFilter: ReportStatusFilter;
  searchQuery: string;
  dateRange: { from?: Date; to?: Date };
  selectedQuickOption: QuickSelectOption | undefined;

  // Delete dialog state
  isDeleteReportDialogOpen: boolean;
  selectedReport: ReportOutput | undefined;
  setIsDeleteReportDialogOpen: (open: boolean) => void;

  // Copy hash state
  copiedHashReportId: string | null;

  // Handlers
  handleSearchQueryChange: (value: string) => void;
  handleDateRangeChange: (newDateRange: DateRangePickerProps['dateRange']) => void;
  handleQuickSelectOption: (option: QuickSelectOption) => void;
  handleDeleteReport: (event: SyntheticEvent, report: ReportOutput) => void;
  handleCopyHash: (hash: string, reportId: string) => Promise<void>;
  handleStatusChange: (status: ReportStatusFilter) => void;
  resetSearch: () => void;
}

export function useReportListFilters(): ReportListFilters {
  const searchParams = useReportSearchParams();
  const dateRange = useReportDateRange();
  const deleteDialog = useDeleteDialog();
  const copyHash = useCopyHash();

  const resetSearch = useCallback(() => {
    dateRange.resetDateRange();
    searchParams.resetSearchParams();
  }, [dateRange, searchParams]);

  return {
    apiStatus: searchParams.apiStatus,
    apiSearchQuery: searchParams.apiSearchQuery,
    from: dateRange.from,
    to: dateRange.to,
    statusFilter: searchParams.statusFilter,
    searchQuery: searchParams.searchQuery,
    dateRange: dateRange.dateRange,
    selectedQuickOption: dateRange.selectedQuickOption,
    isDeleteReportDialogOpen: deleteDialog.isDeleteReportDialogOpen,
    selectedReport: deleteDialog.selectedReport,
    setIsDeleteReportDialogOpen: deleteDialog.setIsDeleteReportDialogOpen,
    copiedHashReportId: copyHash.copiedHashReportId,
    handleSearchQueryChange: searchParams.handleSearchQueryChange,
    handleDateRangeChange: dateRange.handleDateRangeChange,
    handleQuickSelectOption: dateRange.handleQuickSelectOption,
    handleDeleteReport: deleteDialog.handleDeleteReport,
    handleCopyHash: copyHash.handleCopyHash,
    handleStatusChange: searchParams.handleStatusChange,
    resetSearch,
  };
}
